import type { Organization } from "~care/types";
import type { CareGiverLeadFormData } from "~care/types/forms/care-giver-lead-form";

import { AnimatePresence, motion } from "motion/react";
import { useFormContext, useWatch } from "react-hook-form";
import { FormError } from "../../FormError/FormError";
import { Input } from "../../Input/Input";
import { LocationSelect } from "../../LocationSelect/LocationSelect";

export type LocationSelectionState =
  | "local" // franchise pre-selected, nothing to show
  | "idle" // user hasn’t typed a postal code yet
  | "loading"
  | "error"
  | "no-locations"
  | "has-locations"; // we have nearby orgs to choose from

interface LocationSectionProps {
  locationSelectionState: LocationSelectionState;
  franchiseOptions: Organization[];
  setLocationSelectOpen: (open: boolean) => void;
  locationSelectOpen: boolean;
}

export const LocationSection = ({
  locationSelectionState,
  franchiseOptions,
  setLocationSelectOpen,
  locationSelectOpen,
}: LocationSectionProps) => {
  const {
    register,
    formState: { errors },
  } = useFormContext<CareGiverLeadFormData>();

  const postalCode = useWatch({ name: "step2.postalCode" });

  return (
    <>
      <input
        type="hidden"
        value={postalCode}
        {...register("step2.postalCode")}
      />
      <Input
        id="postal-input"
        maxLength={10}
        placeholder="Postal Code"
        type="text"
        {...register("step2.userEnteredLocation")}
        className="bg-white text-base text-new-beginnings-base"
      />

      {locationSelectionState === "loading" && (
        <p className="text-muted-foreground text-sm">
          Searching for locations…
        </p>
      )}

      {locationSelectionState === "error" && (
        <FormError.p>Failed to load locations.</FormError.p>
      )}

      {locationSelectionState === "no-locations" && (
        <>
          <FormError.span>
            {errors?.step2?.userEnteredLocation?.message}
          </FormError.span>

          <p className="text-error text-sm">No locations found.</p>
        </>
      )}

      {locationSelectionState === "has-locations" && (
        <AnimatePresence mode="wait">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            initial={{ opacity: 0, y: -5 }}
            key="location-select"
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <LocationSelect
              franchiseOptions={franchiseOptions}
              onOpenChange={setLocationSelectOpen}
              open={locationSelectOpen}
              organizationNumberFieldName="step2.organizationNumber"
              postalCodeFieldName="step2.postalCode"
            />
          </motion.div>
        </AnimatePresence>
      )}
    </>
  );
};
